import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Settings > Texting and Email > Season check-in message (migration 098).
//
// What Remind on the Dashboard season tile sends to customers who have not
// answered the season question: a text to those who agreed to texts when
// texting is on (the message on one line, with "Reply STOP to opt out." added),
// an email to everyone else with an address. It must carry both
// {{season_yes_link}} and {{season_no_link}}. GET/PUT
// /api/company/season-checkin; Preview is POST
// /api/company/season-checkin/preview, which merges the boxes as they stand
// (saved or not) against a sample customer. Reset to default refills the
// boxes; Save stores it, and a box left at the default is stored as "use the
// default".
function SeasonCheckinCard() {
  const [loaded, setLoaded] = useState(null);
  const [subject, setSubject] = useState('');
  const [template, setTemplate] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const apply = (data) => {
    setLoaded(data);
    setSubject(data.subject || data.defaultSubject);
    setTemplate(data.template || data.defaultTemplate);
  };

  useEffect(() => {
    let live = true;
    axios.get(`${API_URL}/api/company/season-checkin`)
      .then((res) => { if (live) apply(res.data); })
      .catch((err) => { if (live) setMessage({ type: 'error', text: err.response?.data?.error || 'Could not load the season check-in message' }); });
    return () => { live = false; };
  }, []);

  if (!loaded) {
    return (
      <div className="card mb-lg">
        <div className="card-header"><h2 className="card-title">Season Check-in Message</h2></div>
        {message.text ? <div className="alert alert-error">{message.text}</div> : <p className="text-muted">Loading...</p>}
      </div>
    );
  }

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await axios.put(`${API_URL}/api/company/season-checkin`, { subject, template });
      apply(res.data);
      setMessage({ type: 'success', text: 'Season check-in message saved.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Could not save the season check-in message' });
    } finally {
      setSaving(false);
    }
  };

  const showPreview = async () => {
    setPreviewing(true);
    try {
      const res = await axios.post(`${API_URL}/api/company/season-checkin/preview`, { subject, template });
      setPreview(res.data);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Could not build the preview' });
    } finally {
      setPreviewing(false);
    }
  };

  const resetToDefault = () => {
    setSubject(loaded.defaultSubject);
    setTemplate(loaded.defaultTemplate);
    setPreview(null);
  };

  const atDefault = subject.trim() === loaded.defaultSubject && template.trim() === loaded.defaultTemplate.trim();

  return (
    <div className="card mb-lg">
      <div className="card-header">
        <h2 className="card-title">Season Check-in Message</h2>
        <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
          Sent when you press Remind on the Dashboard season tile, to customers who have not answered for
          this winter. If your texting is set up and active, customers who agreed to texts get it as a text
          (with "Reply STOP to opt out." added); everyone else with an email address gets an email. Keep it
          short: each text segment is billed.
        </p>
      </div>

      <form onSubmit={save}>
        {message.text && <div className={`alert alert-${message.type}`} aria-live="polite">{message.text}</div>}

        <div className="form-group">
          <label className="form-label" htmlFor="seasonCheckinSubject">Email subject</label>
          <input
            id="seasonCheckinSubject"
            type="text"
            className="form-input"
            value={subject}
            maxLength={120}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="seasonCheckinTemplate">Message</label>
          <textarea
            id="seasonCheckinTemplate"
            className="form-textarea"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            rows={6}
          />
          <small className="text-muted" data-testid="season-checkin-fields">
            Must include {'{{season_yes_link}}'} and {'{{season_no_link}}'}. Fields you can use:{' '}
            {loaded.fields.map((f) => `{{${f}}}`).join(', ')}
          </small>
        </div>

        {preview && (
          <div className="disclaimer-preview" data-testid="season-checkin-preview">
            <label className="form-label">Preview (a sample customer)</label>
            <div className="disclaimer-preview-box">
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>{preview.subject}</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{preview.body}</div>
            </div>
            <small className="text-muted">
              Text version{preview.smsSegments ? ` (${preview.smsSegments} segment${preview.smsSegments === 1 ? '' : 's'})` : ''}:{' '}
              {preview.sms || 'too long to send as a text; these customers will be emailed instead'}
            </small>
          </div>
        )}

        <div className="disclaimer-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Check-in Message'}
          </button>
          <button type="button" className="btn btn-outline" onClick={showPreview} disabled={previewing}>
            {previewing ? 'Building...' : 'Preview'}
          </button>
          <button type="button" className="btn btn-outline" onClick={resetToDefault} disabled={atDefault}>
            Reset to default
          </button>
        </div>
      </form>
    </div>
  );
}

export default SeasonCheckinCard;
